/**
 * Where the small picture of a video call sits.
 *
 * The self-view used to snap to whichever of four corners it was let go
 * nearest, which is tidy and wrong: the one thing it has to do is stay off
 * the face — either face — and on a call the interesting half of the frame is
 * never in a corner. So it goes wherever it is dropped, and this file is the
 * arithmetic of "wherever" — kept out of the component because it is pure and
 * worth testing without a DOM.
 *
 * Position is remembered as a *fraction* of the room the square has to move
 * in rather than as pixels: a phone turned on its side keeps the square in the
 * same relative place instead of leaving it half off the screen, and the same
 * numbers survive a keyboard, a rotation and a shell that resizes the WebView.
 * The reverse conversion is done by the CSS — see `--pip-x` in CallOverlay.css
 * — so the square's size stays a stylesheet fact and is never copied here.
 */

/** How close to an edge the square may rest. Mirrored by `--pip-inset`. */
export const PIP_INSET = 14;

/** Below this a press is a tap — a swap — rather than a very short drag. */
export const PIP_TAP_SLOP = 8;

export interface Point {
  x: number;
  y: number;
}

export interface Box {
  width: number;
  height: number;
}

/** The room the square's top-left corner has, once both insets are taken. */
function travel(viewport: Box, size: Box): Box {
  return {
    width: Math.max(0, viewport.width - size.width - PIP_INSET * 2),
    height: Math.max(0, viewport.height - size.height - PIP_INSET * 2),
  };
}

function clamp(value: number, limit: number): number {
  return Math.min(limit, Math.max(0, value));
}

/**
 * Where the square goes while a finger is on it: under the point it was
 * grabbed by, and never past an edge — a square dragged half out of the screen
 * cannot be dragged back, because the half that is left holds no pixels to
 * press.
 */
export function pipDrag(pointer: Point, grab: Point, viewport: Box, size: Box): Point {
  const room = travel(viewport, size);
  return {
    x: PIP_INSET + clamp(pointer.x - grab.x - PIP_INSET, room.width),
    y: PIP_INSET + clamp(pointer.y - grab.y - PIP_INSET, room.height),
  };
}

/** What is remembered when the finger comes off: the same place, as fractions. */
export function pipSpot(place: Point, viewport: Box, size: Box): Point {
  const room = travel(viewport, size);
  return {
    x: room.width === 0 ? 0 : clamp(place.x - PIP_INSET, room.width) / room.width,
    y: room.height === 0 ? 0 : clamp(place.y - PIP_INSET, room.height) / room.height,
  };
}

/** A press that stayed put is a tap, whatever the touchscreen made of it. */
export function isPipTap(from: Point, to: Point): boolean {
  return Math.abs(to.x - from.x) <= PIP_TAP_SLOP && Math.abs(to.y - from.y) <= PIP_TAP_SLOP;
}
